import { useNavigate } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import WebDatatable from 'app/shared/datatable/web/datatable';
import MobileDatatable from 'app/shared/datatable/mobile/datatable';

import { loadCampaignsToStore } from 'store/actions/campaign';
import { setPageTitle } from 'store/actions/header';
import { Button } from 'app/shared/button';
import styles from '../campaign.module.css';
import { useCampaignService } from 'hooks/api/mailing/campaign';
import { useAutomationEventService } from 'hooks/api/automata/event';
import { filter_config } from './config/filter';

const ListCampaigns = () => {
  const navigateTo = useNavigate();
  const dispatch = useDispatch();
  const { fetchCampaigns, searchCampaigns } = useCampaignService();

  const { is_mobile_view } = useSelector((state) => state.metadata);
  const campaigns_in_store = useSelector((state) => state.campaigns);
  const { fetchEvents } = useAutomationEventService();

  const [campaigns, setCampaigns] = useState([]);
  const [is_search_mode, setSearchMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [show_test, setShowTest] = useState(false);
  const [total_campaigns, setTotalCampaigns] = useState(0);
  const [events, setEvents] = useState([]);
  const [mailboxes, setMailboxes] = useState([]);
  const [senderNames, setSenderNames] = useState([]);

  useEffect(() => {
    dispatch(setPageTitle([{ title: 'My Campaigns', path: '.' }]));

    fetchEvents({ query_string: 'type=user' }).then(({ events }) => {
      setEvents(() => events.map((e) => ({ ...e, value: e.code.split(':')[0], label: e.name })));
    });

    fetchCampaigns().then(({ campaigns }) => {
      const emails = campaigns.map((allInfo) => allInfo.sender_email).filter(Boolean);
      const uniqueEmails = [...new Set(emails)];
      setMailboxes(uniqueEmails.map((email) => ({ value: email, label: email })));
    });

    fetchCampaigns().then(({ campaigns }) => {
      const names = campaigns.map((allInfo) => allInfo.sender_name).filter(Boolean);
      const uniqueNames = [...new Set(names)];
      setSenderNames(uniqueNames.map((name) => ({ value: name, label: name })));
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const parsed_campaigns = Object.values(campaigns_in_store);
    setCampaigns(() => parsed_campaigns);
  }, [campaigns_in_store]);

  useEffect(() => {
    handleDataRequest(0);
  }, [show_test]);

  const config = {
    actions: {
      single: () => [
        [
          {
            value: 'view',
            label: 'View'
          }
        ]
      ]
    },
    allow_bulk_action: true,
    css: {},
    fields: [
      {
        title: 'Campaign Name',
        key: 'name',
        sortable: true,
        isTitle: true,
        searchable: true,
        formatter: (value) => (value?.length > 25 ? `${value.substr(0, 25)}...` : value || '')
      },
      {
        title: 'Sender Name',
        key: 'sender_name',
        sortable: true,
        isTagline: true,
        formatter: (value) => value || 'N/A'
      },
      {
        title: 'Sender Email',
        key: 'sender_email',
        sortable: true,
        isTagline: true,
        searchable: true,
        formatter: (value) => value || 'N/A'
      },
      {
        title: 'Status',
        sortable: true,
        key: 'status',
        searchable: true,
        formatter: (value) => {
          switch (value) {
            case 'processed':
              return <span className={styles.greenBadge}>{value}</span>;
            case 'queued':
              return <span className={styles.infoBadge}>{value}</span>;
            case 'cancelled':
              return <span className={styles.dangerBadge}>{value}</span>;
            default:
              return <span className={styles.dangerBadge}>{value}</span>;
          }
        },
        isBadge: true
      },
      {
        title: 'Send date',
        key: 'schedule',
        sortable: true,
        formatter: (value) => {
          if (!value?.date) return '...';
          return new Date(value.date).toDateString();
        },
        isMetadata: true
      }
    ],
    is_search_mode,
    items: campaigns,
    search_key: 'name',
    search_text: ''
  };

  const handleDatatableAction = (payload) => {
    const { name, type, data } = payload;

    if (type === 'single') {
      switch (name) {
        default:
          navigateTo(`/campaigns/${data.id}`);
      }
    }
  };

  const handleItemClick = (payload) => {
    const { id } = payload;
    navigateTo(`/campaigns/${id}`);
  };

  const handleDataRequest = async (
    page,
    population = 10,
    options = { filter_query: '&', sort_query: '' }
  ) => {
    try {
      setLoading(true);
      const query =
        !options.filter_query || options.filter_query === '&'
          ? 'bool=-is_test_campaign'
          : options.filter_query;
      const sort_query = options.sort_query || 'sort_by=-created_on';

      const { campaigns, size } = await fetchCampaigns({
        query_string: `${query}&page=${page}&population=${population}&${sort_query}&return_only=is_advanced,id,name,sender_name,sender_email,status,schedule,failed_sends_list,created_on,is_test_campaign`
      });

      setTotalCampaigns(size);
      dispatch(loadCampaignsToStore(campaigns));
    } catch (e) {
      dispatch(loadCampaignsToStore([]));
    } finally {
      setLoading(false);
    }
  };

  const handleSearchRequest = async (
    keys,
    keyword,
    page,
    population = 10,
    options = { filter_query: '&', sort_query: '' }
  ) => {
    if (!keys) return;
    try {
      setLoading(true);
      const query =
        !options.filter_query || options.filter_query === '&'
          ? 'bool=-is_test_campaign'
          : options.filter_query;
      const sort_query = options.sort_query || 'sort_by=-created_on';

      const { campaigns, size } = await searchCampaigns(keys, keyword, {
        query_string: `${query}&page=${page}&population=${population}&${sort_query}&return_only=id,name,sender_name,sender_email,status,schedule,failed_sends_list,created_on,is_test_campaign`
      });

      setTotalCampaigns(size);
      dispatch(loadCampaignsToStore(campaigns));
    } catch (e) {
      dispatch(loadCampaignsToStore([]));
    } finally {
      setLoading(false);
    }
  };

  const table_actions = (
    <>
      <Button
        icon_name={show_test ? 'hide' : 'show'}
        text={`${show_test ? 'Hide' : 'Show'} test campaigns`}
        onClick={() => setShowTest((show) => !show)}
      />
      <Button icon_name="add" text="Campaign" onClick={() => navigateTo('/editor/campaign/')} />
    </>
  );

  return (
    <div>
      {is_mobile_view ? (
        <MobileDatatable
          config={config}
          action={handleDatatableAction}
          onClick={handleItemClick}
          onListModeChange={setSearchMode}
          onDataRequest={handleDataRequest}
          onSearchRequest={handleSearchRequest}
        />
      ) : (
        <WebDatatable
          config={{
            ...config,
            total_count: total_campaigns
          }}
          action={handleDatatableAction}
          onClick={handleItemClick}
          checkbox
          has_filter={true}
          filter_config={filter_config(events, mailboxes, senderNames)}
          loading_data={loading}
          table_actions={table_actions}
          onDataRequest={handleDataRequest}
          onSearchRequest={handleSearchRequest}
        />
      )}
    </div>
  );
};

export default ListCampaigns;
